import { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import Page from "./Page";
import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

const OrdersMonitoringHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { executeAction } = useData();
  const { showMessage } = usePage();

  const clientId = location?.state?.clientId || null;
  const clientName = location?.state?.clientName || "";
  const returnPath = location?.state?.returnPath || "/clientorders";
  const returnState = location?.state?.returnState || {};

  const [loading, setLoading] = useState(false);
  const [submissionRecords, setSubmissionRecords] = useState([]);
  const [transmissionRecords, setTransmissionRecords] = useState([]);
  const [receiptRecords, setReceiptRecords] = useState([]);

  useEffect(() => {
    loadSummary();
  }, [clientId]);

  const summary = useMemo(() => {
    const transmissionStatusCounts = {
      queued: 0,
      transmitting: 0,
      transmitted: 0,
      acknowledged: 0,
      rejected: 0,
      retry_pending: 0,
      failed_final: 0,
    };

    transmissionRecords.forEach((item) => {
      const status = item?.status;
      if (
        status &&
        Object.prototype.hasOwnProperty.call(transmissionStatusCounts, status)
      ) {
        transmissionStatusCounts[status] += 1;
      }
    });

    const receiptStatusCounts = {
      accepted: 0,
      rejected: 0,
      pending: 0,
      error: 0,
    };

    receiptRecords.forEach((item) => {
      const status = item?.receiptStatus;
      if (
        status &&
        Object.prototype.hasOwnProperty.call(receiptStatusCounts, status)
      ) {
        receiptStatusCounts[status] += 1;
      }
    });

    const acceptedSubmissions = submissionRecords.filter(
      (item) => item?.acceptedByServer === true,
    ).length;

    return {
      submissionsTotal: submissionRecords.length,
      acceptedSubmissions,
      transmissionsTotal: transmissionRecords.length,
      transmissionStatusCounts,
      receiptsTotal: receiptRecords.length,
      receiptStatusCounts,
    };
  }, [submissionRecords, transmissionRecords, receiptRecords]);

  const loadSummary = async () => {
    if (!clientId) {
      setSubmissionRecords([]);
      setTransmissionRecords([]);
      setReceiptRecords([]);
      return;
    }

    setLoading(true);
    try {
      const [submissionResults, transmissionResults, receiptResults] =
        await Promise.all([
          executeAction({
            entityName: "OrderSubmission",
            action: "get",
            filter: { clientId },
          }),
          executeAction({
            entityName: "OrderTransmission",
            action: "get",
            filter: { clientId },
          }),
          executeAction({
            entityName: "OrderTransmissionReceipt",
            action: "get",
            filter: { clientId },
          }),
        ]);

      setSubmissionRecords(
        submissionResults?.isSuccessful && Array.isArray(submissionResults.data)
          ? submissionResults.data
          : [],
      );
      setTransmissionRecords(
        transmissionResults?.isSuccessful &&
          Array.isArray(transmissionResults.data)
          ? transmissionResults.data
          : [],
      );
      setReceiptRecords(
        receiptResults?.isSuccessful && Array.isArray(receiptResults.data)
          ? receiptResults.data
          : [],
      );
    } catch (err) {
      setSubmissionRecords([]);
      setTransmissionRecords([]);
      setReceiptRecords([]);
      showMessage(err?.message || "Failed to load monitoring summary", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleToolbarClick = (name) => {
    if (name === "back") {
      navigate(returnPath, { state: returnState });
      return;
    }

    if (name === "refresh") {
      loadSummary();
    }
  };

  const goToPage = (path) => {
    navigate(path, {
      state: {
        clientId,
        clientName,
        returnPath: "/orders/monitor",
        returnState: { ...location.state },
      },
    });
  };

  return (
    <Page
      pageName="Orders Monitoring"
      entityName={clientName || ""}
      includeToolbar
      onToolItemClick={handleToolbarClick}
      items={[
        { name: "back", label: "Back", iconName: "ArrowBack" },
        { name: "refresh", label: "Refresh", iconName: "Refresh" },
      ]}
    >
      {!clientId ? (
        <Typography>Client context is missing for this page.</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography>
            Monitor submissions, transmissions, and acknowledgements from one
            place.
          </Typography>

          {loading ? (
            <Typography>Loading monitoring summary...</Typography>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 1,
                }}
              >
                <Box sx={{ border: "1px solid #ddd", borderRadius: 1, p: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>Submissions</Typography>
                  <Typography>Total: {summary.submissionsTotal}</Typography>
                  <Typography>
                    Accepted: {summary.acceptedSubmissions}
                  </Typography>
                </Box>

                <Box sx={{ border: "1px solid #ddd", borderRadius: 1, p: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>
                    Transmissions
                  </Typography>
                  <Typography>Total: {summary.transmissionsTotal}</Typography>
                  <Typography>
                    Queued/Transmitting:{" "}
                    {summary.transmissionStatusCounts.queued +
                      summary.transmissionStatusCounts.transmitting}
                  </Typography>
                  <Typography>
                    Sent/Ack:{" "}
                    {summary.transmissionStatusCounts.transmitted +
                      summary.transmissionStatusCounts.acknowledged}
                  </Typography>
                  <Typography>
                    Failed/Rejected:{" "}
                    {summary.transmissionStatusCounts.failed_final +
                      summary.transmissionStatusCounts.rejected +
                      summary.transmissionStatusCounts.retry_pending}
                  </Typography>
                </Box>

                <Box sx={{ border: "1px solid #ddd", borderRadius: 1, p: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>Receipts</Typography>
                  <Typography>Total: {summary.receiptsTotal}</Typography>
                  <Typography>
                    Accepted: {summary.receiptStatusCounts.accepted}
                  </Typography>
                  <Typography>
                    Rejected: {summary.receiptStatusCounts.rejected}
                  </Typography>
                  <Typography>
                    Pending: {summary.receiptStatusCounts.pending}
                  </Typography>
                  <Typography>
                    Error: {summary.receiptStatusCounts.error}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={() => goToPage("/orders/monitor/submissions")}
                >
                  Open Submissions
                </Button>
                <Button
                  variant="contained"
                  onClick={() => goToPage("/orders/monitor/receipts")}
                >
                  Open Receipts
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}
    </Page>
  );
};

export default OrdersMonitoringHome;
